import type { ReviewFlavor } from '@chatsdet/types';

/** Literal union of the Labs list routes for review flavors — kept as a
 * literal type (not `string`) so it stays checkable against the TanStack
 * Router route tree at `navigate()`/`<Link>` call sites. */
export type ReviewFlavorListPath = '/runs/ux-review' | '/runs/spec-review';

/** Literal union of the Labs detail routes for review flavors. */
export type ReviewFlavorDetailPath =
  | '/runs/ux-review/$exploratoryTestId'
  | '/runs/spec-review/$exploratoryTestId';

/**
 * Client-side, copy/routing-only metadata for the review-only exploratory
 * flavors (everything except `functional`). Report sidebars stay bespoke per
 * flavor (see `UxReviewSidebar` / `SpecReviewSidebar`) — this registry only
 * centralizes the launcher/list/nav strings and routing so a new flavor
 * doesn't require touching every callsite individually.
 */
export type WebReviewFlavorMeta = {
  key: 'ux' | 'spec';
  featureKey: 'uxReview' | 'specReview';
  /** Labs list route, e.g. '/runs/ux-review'. */
  listPath: ReviewFlavorListPath;
  /** Labs detail route template, e.g. '/runs/ux-review/$exploratoryTestId'. */
  detailPath: ReviewFlavorDetailPath;
  /** Breadcrumb label for the list crumb on the detail page. */
  breadcrumbLabel: string;
  /** Option label shown in the launcher's Mode select. */
  modeOptionLabel: string;
  /** Launcher dialog copy, used when `lockFlavor` is set. */
  dialogTitle: string;
  dialogEyebrow: string;
  dialogDescription: string;
  /** Instructions field copy. */
  instructionsLabel: string;
  instructionsPlaceholder: string;
};

export const WEB_REVIEW_FLAVORS: Record<'ux' | 'spec', WebReviewFlavorMeta> = {
  ux: {
    key: 'ux',
    featureKey: 'uxReview',
    listPath: '/runs/ux-review',
    detailPath: '/runs/ux-review/$exploratoryTestId',
    breadcrumbLabel: 'UX Review',
    modeOptionLabel: 'UX walkthrough (graded)',
    dialogTitle: 'New UX review',
    dialogEyebrow: 'UX review',
    dialogDescription:
      'A persona walks through your app to accomplish a goal, then grades the experience and files findings with a video recording.',
    instructionsLabel: 'Who is the user, and what do they want?',
    instructionsPlaceholder:
      'Describe the user and their goal — e.g. "A busy parent on their phone trying to find store hours and call the location."',
  },
  spec: {
    key: 'spec',
    featureKey: 'specReview',
    listPath: '/runs/spec-review',
    detailPath: '/runs/spec-review/$exploratoryTestId',
    breadcrumbLabel: 'Spec Review',
    modeOptionLabel: 'Spec review (feasibility)',
    dialogTitle: 'New Spec Review',
    dialogEyebrow: 'Spec review',
    dialogDescription:
      'Paste a loose test spec — the agent explores the live app to verify what can and cannot be tested, and returns a cleaned-up, runnable spec.',
    instructionsLabel: 'Test spec',
    instructionsPlaceholder:
      'Paste the test spec to review — steps, assumptions, and expected results in any format.',
  },
};

/** Ordered list for iterating the registry deterministically (Mode select, nav). */
export const WEB_REVIEW_FLAVOR_LIST: WebReviewFlavorMeta[] = [
  WEB_REVIEW_FLAVORS.ux,
  WEB_REVIEW_FLAVORS.spec,
];

/** Looks up registry metadata for a flavor; `null` for `functional`/undefined. */
export function getWebReviewFlavor(
  flavor: ReviewFlavor | undefined | null
): WebReviewFlavorMeta | null {
  if (flavor === 'ux' || flavor === 'spec') return WEB_REVIEW_FLAVORS[flavor];
  return null;
}

/**
 * True for the Exploratory section: `/exploratory` itself plus the review
 * flavors' list and detail routes. New UX/Spec review runs open on the latter,
 * so the sidebar must keep Exploratory highlighted and no section sub-nav shows.
 */
export function isExploratoryPath(pathname: string): boolean {
  if (pathname === '/exploratory' || pathname.startsWith('/exploratory/')) return true;
  return WEB_REVIEW_FLAVOR_LIST.some(
    ({ listPath }) => pathname === listPath || pathname.startsWith(`${listPath}/`)
  );
}
